export enum BillingSubscriptionState {
  Active = 'ACTIVE',
  CancellationScheduled = 'CANCELLATION_SCHEDULED',
  PaymentRecovery = 'PAYMENT_RECOVERY',
  ChangeScheduled = 'CHANGE_SCHEDULED',
  Processing = 'PROCESSING',
  Unavailable = 'UNAVAILABLE',
}

export interface BillingSubscription {
  state: BillingSubscriptionState;
  itemName: string | null;
  renewalDate: Date | null;
  endDate: Date | null;
}

export interface GetBillingSubscriptionsResponse {
  subscriptions: BillingSubscription[];
}

export enum BillingInvoiceState {
  Paid = 'PAID',
  AwaitingPayment = 'AWAITING_PAYMENT',
  Failed = 'FAILED',
  Voided = 'VOIDED',
  Other = 'OTHER',
}

export interface BillingInvoice {
  id: string;
  invoiceNumber: string | null;
  state: BillingInvoiceState;
  totalAmount: number | null;
  currency: string | null;
  minorUnits: number | null;
  issuedAt: Date | null;
}

export interface GetBillingInvoicesResponse {
  invoices: BillingInvoice[];
}

interface RawBillingSubscription {
  state?: unknown;
  itemName?: unknown;
  renewalDate?: unknown;
  endDate?: unknown;
}

interface RawBillingSubscriptionsData {
  subscriptions?: unknown;
}

interface RawBillingInvoice {
  id?: unknown;
  invoiceNumber?: unknown;
  state?: unknown;
  totalAmount?: unknown;
  currency?: unknown;
  minorUnits?: unknown;
  issuedAt?: unknown;
}

interface RawBillingInvoicesData {
  invoices?: unknown;
}

export function parseBillingSubscriptionResponse(
  data: unknown
): GetBillingSubscriptionsResponse {
  const subscriptionsData = data as RawBillingSubscriptionsData | undefined;
  if (!subscriptionsData || !Array.isArray(subscriptionsData.subscriptions)) {
    return { subscriptions: [] };
  }
  return {
    subscriptions: subscriptionsData.subscriptions.map(
      (sub: RawBillingSubscription): BillingSubscription => ({
        state:
          typeof sub?.state === 'string' &&
          Object.values(BillingSubscriptionState).includes(
            sub.state as BillingSubscriptionState
          )
            ? (sub.state as BillingSubscriptionState)
            : BillingSubscriptionState.Unavailable,
        itemName: typeof sub?.itemName === 'string' ? sub.itemName : null,
        renewalDate:
          typeof sub?.renewalDate === 'string' ||
          sub?.renewalDate instanceof Date
            ? new Date(sub.renewalDate)
            : null,
        endDate:
          typeof sub?.endDate === 'string' || sub?.endDate instanceof Date
            ? new Date(sub.endDate)
            : null,
      })
    ),
  };
}

export function parseBillingInvoicesResponse(
  data: unknown
): GetBillingInvoicesResponse {
  const invoicesData = data as RawBillingInvoicesData | undefined;
  if (!invoicesData || !Array.isArray(invoicesData.invoices)) {
    return { invoices: [] };
  }
  return {
    invoices: invoicesData.invoices.map(
      (inv: RawBillingInvoice): BillingInvoice => ({
        id: typeof inv?.id === 'string' ? inv.id : String(inv?.id ?? ''),
        invoiceNumber:
          typeof inv?.invoiceNumber === 'string' ? inv.invoiceNumber : null,
        state:
          typeof inv?.state === 'string' &&
          Object.values(BillingInvoiceState).includes(
            inv.state as BillingInvoiceState
          )
            ? (inv.state as BillingInvoiceState)
            : BillingInvoiceState.Other,
        totalAmount:
          typeof inv?.totalAmount === 'number' ? inv.totalAmount : null,
        currency: typeof inv?.currency === 'string' ? inv.currency : null,
        minorUnits: typeof inv?.minorUnits === 'number' ? inv.minorUnits : null,
        issuedAt:
          typeof inv?.issuedAt === 'string' || inv?.issuedAt instanceof Date
            ? new Date(inv.issuedAt)
            : null,
      })
    ),
  };
}
